import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Input, Select, Space, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import GcPagina from '../Layout/GcPagina';
import {
  actualizarCliente,
  buscarClientesPorCodigo,
  compararCliente,
  crearCliente,
  cuerpoError,
  estadoError,
  leerClienteCaso,
  leerLecturaCaso,
  mensajeError,
  nuevoIdPeticion,
} from '../../api/feriasApi';
import type { ClienteCaso, ComparacionCliente, CriteriosCliente, ResumenCliente, SeleccionPerfil, ValorCampo } from '../../api/feriasApi';
import { FctAviso, FctEstado, FctIcono } from '../../fct/react';
import { BuscadorCliente } from './BuscadorCliente';
import { etiquetas, opciones, perfilDeLaFicha, PLAZOS, PROVINCIAS, TRAMOS } from './clienteCatalogos';
import {
  cambios,
  codigoCl,
  derechaDe,
  derechaNueva,
  faltanParaCrear,
  FILAS,
  FILAS_INFORMATIVAS,
  mismoPerfil,
  mismoValor,
  nombreFaltante,
  texto,
  vaciosQueNoBorran,
  valoresActuales,
} from './clienteFilas';
import type { Valores } from './clienteFilas';
import { ConfirmarGuardado } from './ConfirmarGuardado';
import type { Accion } from './ConfirmarGuardado';
import { motivoEnPantalla } from './motivos';
import { SIN_DATO } from './textos';
import './Ferias.css';

// La marca de una fila: si la ficha y la columna de Gescom dicen lo mismo (manual: icono y etiqueta).
function Marca({ izquierda, coincide }: { izquierda: boolean; coincide: boolean }) {
  if (!izquierda) {
    return <span className="ferias-cliente__vacio">Vacío en la ficha</span>;
  }
  return (
    <FctEstado
      personalizado={
        coincide ? { etiqueta: 'Coincide', familia: 'exito', icono: 'check' } : { etiqueta: 'Distinto', familia: 'aviso', icono: 'alerta' }
      }
    />
  );
}

// El perfil actual de una comparación como selección.
const perfilActualDe = (c: ComparacionCliente): SeleccionPerfil => ({
  plazo: c.perfil.plazoActual,
  tramos: c.perfil.tramosActuales,
  provincias: c.perfil.provinciasActuales,
});

/**
 * El paso de cliente de un caso (#98 de fct-ferias): la ficha a la izquierda y el cliente de Gescom a la
 * derecha, campo a campo, como en la verificación. El CL de la ficha carga su cliente; una búsqueda
 * encuentra otro; sin código, la columna de Gescom es un cliente nuevo. Un solo «Guardar» actualiza o
 * crea, tras una confirmación. Un caso con el paso guardado muestra su cliente, y se puede corregir y
 * guardar de nuevo (D39). Lo que manda lo comprueba la API (D37, D38), y la pantalla muestra sus rechazos.
 */
const ClienteCasoPagina: React.FC = () => {
  const idCaso = Number(useParams().idCaso);
  const navegar = useNavigate();
  const [ficha, setFicha] = useState<Record<string, ValorCampo> | null>(null);
  const [comparacion, setComparacion] = useState<ComparacionCliente | null>(null);
  const [derecha, setDerecha] = useState<Valores>({});
  const [perfil, setPerfil] = useState<SeleccionPerfil>({ plazo: null, tramos: [], provincias: [] });
  // El cliente guardado del caso (D39: el paso sigue abierto y se guarda otra vez).
  const [vinculado, setVinculado] = useState<ClienteCaso | null>(null);
  // El aviso tras un guardado: si ese mismo guardado ya estaba hecho (CA21).
  const [guardado, setGuardado] = useState<{ yaGuardado: boolean } | null>(null);
  const [buscador, setBuscador] = useState<{ abierto: boolean; iniciales: ResumenCliente[] | null }>({ abierto: false, iniciales: null });
  const [aviso, setAviso] = useState<string | null>(null);
  const [cambiados, setCambiados] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accion, setAccion] = useState<Accion | null>(null);
  const [errorGuardado, setErrorGuardado] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  // Un segundo clic antes de la primera respuesta no manda nada (CA21).
  const enCurso = useRef(false);

  // El perfil de compra de la ficha como ids de catálogo.
  const perfilFicha = useMemo(() => (ficha ? perfilDeLaFicha(ficha.plazo, ficha.facturacion, ficha.provincias) : null), [ficha]);

  // Las cajas de la búsqueda empiezan con los valores de la ficha.
  const criterios = useMemo<Partial<CriteriosCliente>>(
    () =>
      ficha
        ? {
            codigo: codigoCl(ficha.cl) ?? '',
            nombre: ['nombre', 'apellido1', 'apellido2']
              .map((k) => texto(ficha[k]))
              .filter((v) => v !== '')
              .join(' '),
            email: texto(ficha.email),
            telefono: '',
            movil: texto(ficha.movil),
          }
        : {},
    [ficha],
  );

  // Un cliente de Gescom en la columna de la derecha, con sus valores actuales.
  const cargarCliente = useCallback(
    async (idCliente: number, valores: Record<string, ValorCampo>) => {
      try {
        const cargada = await compararCliente(idCaso, idCliente);
        setComparacion(cargada);
        setDerecha(derechaDe(cargada, valores));
        setPerfil(perfilActualDe(cargada));
        setCambiados(null);
        setAviso(null);
        setBuscador({ abierto: false, iniciales: null });
      } catch (e) {
        setError(motivoEnPantalla(mensajeError(e)));
      }
    },
    [idCaso],
  );

  // Un cliente nuevo en la columna de la derecha, con los valores de la ficha.
  const nuevo = useCallback((valores: Record<string, ValorCampo>) => {
    const p = perfilDeLaFicha(valores.plazo, valores.facturacion, valores.provincias);
    setComparacion(null);
    setDerecha(derechaNueva(valores));
    setPerfil({ plazo: p.plazo, tramos: p.tramos, provincias: p.provincias });
    setCambiados(null);
  }, []);

  // Al abrir: un caso guardado carga su cliente (D39); si no, el CL de la ficha carga su cliente, o se abre
  // la búsqueda.
  useEffect(() => {
    const empezar = async () => {
      const paso = await leerClienteCaso(idCaso);
      const valores = (await leerLecturaCaso(idCaso)).verificacion?.valores;
      if (!valores) {
        setError(`La ficha #${idCaso} aún no está verificada: confirme su lectura antes de buscar el cliente.`);
        return;
      }
      setFicha(valores);
      nuevo(valores);
      if (paso.guardado) {
        setVinculado(paso);
        if (paso.idCliente !== null) {
          await cargarCliente(paso.idCliente, valores);
        }
        return;
      }
      const codigo = codigoCl(valores.cl);
      if (!codigo) {
        setBuscador({ abierto: true, iniciales: null });
        return;
      }
      const conEseCodigo = await buscarClientesPorCodigo(codigo);
      if (conEseCodigo.length === 1) {
        await cargarCliente(conEseCodigo[0].id, valores);
      } else if (conEseCodigo.length > 1) {
        setBuscador({ abierto: true, iniciales: conEseCodigo });
      } else {
        setAviso(`Ningún cliente de Gescom tiene el código ${codigo} de la ficha.`);
        setBuscador({ abierto: true, iniciales: null });
      }
    };
    empezar().catch((e: unknown) => setError(motivoEnPantalla(mensajeError(e))));
  }, [idCaso, cargarCliente, nuevo]);

  // Un campo de la columna de Gescom.
  const poner = (clave: string, valor: string) => setDerecha((actual) => ({ ...actual, [clave]: valor }));

  // Cada valor de la ficha que difiere pasa a la derecha, también las partes del perfil de compra.
  const pasarTodos = () => {
    if (!ficha || !perfilFicha) {
      return;
    }
    setDerecha((actual) => {
      const siguiente = { ...actual };
      FILAS.forEach(({ clave }) => {
        const izquierda = texto(ficha[clave]);
        if (izquierda !== '' && !mismoValor(clave, izquierda, actual[clave] ?? '')) {
          siguiente[clave] = izquierda;
        }
      });
      return siguiente;
    });
    setPerfil((actual) => ({
      plazo: perfilFicha.plazo ?? actual.plazo,
      tramos: perfilFicha.tramos.length > 0 ? perfilFicha.tramos : actual.tramos,
      provincias: perfilFicha.provincias.length > 0 ? perfilFicha.provincias : actual.provincias,
    }));
  };

  // El cliente del caso hasta ahora, cuando un guardado enlazaría el caso con otro (D39).
  const anteriorSi = (otro: number | null) =>
    vinculado?.guardado && vinculado.idCliente !== null && vinculado.idCliente !== otro ? vinculado.codigo || `nº ${vinculado.idCliente}` : null;

  // Guardar: la confirmación dice lo que se va a hacer.
  const preparar = () => {
    setErrorGuardado(null);
    if (comparacion) {
      const cambiaPerfil = !mismoPerfil(perfil, perfilActualDe(comparacion));
      setAccion({
        tipo: 'actualizar',
        codigo: comparacion.codigo ?? '',
        nombre: comparacion.nombreCompleto,
        cambios: cambios(comparacion, derecha),
        vacios: vaciosQueNoBorran(comparacion, derecha),
        perfil: cambiaPerfil ? perfil : null,
        reactiva: cambiaPerfil && comparacion.perfil.dadoDeBaja,
        anterior: anteriorSi(comparacion.idCliente),
      });
    } else {
      setAccion({ tipo: 'crear', valores: derecha, perfil, faltan: faltanParaCrear(derecha), anterior: anteriorSi(null) });
    }
  };

  // Gescom cambió el cliente después de cargarlo (M4-02): los campos que cambió toman su valor nuevo, se
  // quedan las demás ediciones del supervisor, y la pantalla los nombra.
  const revisarCambios = (fresca: ComparacionCliente) => {
    if (!comparacion || !ficha) {
      return;
    }
    const nuevos = derechaDe(fresca, ficha);
    const cambiadas = FILAS.filter(
      ({ clave }) => fresca.campos.find((f) => f.clave === clave)?.actual !== comparacion.campos.find((f) => f.clave === clave)?.actual,
    );
    setDerecha((actual) => ({ ...actual, ...Object.fromEntries(cambiadas.map(({ clave }) => [clave, nuevos[clave]])) }));
    const perfilCambio = !mismoPerfil(perfilActualDe(comparacion), perfilActualDe(fresca)) || comparacion.perfil.dadoDeBaja !== fresca.perfil.dadoDeBaja;
    if (perfilCambio) {
      setPerfil(perfilActualDe(fresca));
    }
    setComparacion(fresca);
    setCambiados([...cambiadas.map((f) => f.etiqueta), ...(perfilCambio ? ['Perfil de compra'] : [])]);
  };

  const confirmar = async () => {
    if (!accion || enCurso.current) {
      return;
    }
    enCurso.current = true;
    setGuardando(true);
    // Un id por confirmación: un reintento de este guardado se distingue de uno nuevo (D39, CA21).
    const idPeticion = nuevoIdPeticion();
    try {
      const resultado =
        accion.tipo === 'actualizar' && comparacion
          ? await actualizarCliente(idCaso, {
              idCliente: comparacion.idCliente,
              valores: Object.fromEntries(accion.cambios.map((c) => [c.clave, c.despues])),
              perfil: accion.perfil,
              token: comparacion.token,
              idPeticion,
            })
          : await crearCliente(idCaso, {
              valores: Object.fromEntries(
                Object.entries(derecha)
                  .map(([clave, valor]) => [clave, valor.trim()])
                  .filter(([, valor]) => valor !== ''),
              ),
              perfil,
              idPeticion,
            });
      // El paso sigue abierto (D39): el cliente guardado se vuelve a cargar a la derecha.
      const paso = await leerClienteCaso(idCaso);
      setAccion(null);
      setVinculado(paso);
      if (paso.idCliente !== null && ficha) {
        await cargarCliente(paso.idCliente, ficha);
      }
      setGuardado({ yaGuardado: resultado.yaGuardado });
    } catch (e) {
      const cuerpo = cuerpoError(e);
      const fresca = estadoError(e) === 409 ? (cuerpo.comparacion as ComparacionCliente | undefined) : undefined;
      if (fresca) {
        setAccion(null);
        revisarCambios(fresca);
      } else if (Array.isArray(cuerpo.faltan)) {
        setErrorGuardado(`Hacen falta: ${(cuerpo.faltan as string[]).map(nombreFaltante).join(', ')}.`);
      } else {
        setErrorGuardado(motivoEnPantalla(mensajeError(e)));
      }
    } finally {
      enCurso.current = false;
      setGuardando(false);
    }
  };

  const pagina = (contenido: ReactNode) => (
    <GcPagina
      seccion={['Ferias', 'Bandeja']}
      titulo={`Cliente de la ficha #${idCaso}`}
      contexto={
        vinculado?.guardado
          ? `Guardado en Gescom: ${vinculado.codigo || `nº ${vinculado.idCliente}`}`
          : comparacion
            ? `Cliente ${comparacion.codigo || 'sin código'} de Gescom`
            : 'Cliente nuevo'
      }
      acciones={
        <Space>
          <Button onClick={() => navegar('/ferias')}>Volver a la bandeja</Button>
          <Button onClick={() => navegar(`/ferias/casos/${idCaso}`)}>Ver la verificación</Button>
          {/* M6 §2, P1: con el cliente guardado, el caso se trata en «Acciones». */}
          {vinculado?.guardado && <Button onClick={() => navegar(`/ferias/casos/${idCaso}/acciones`)}>Ir a acciones</Button>}
          {ficha && (
            <Button type="primary" onClick={preparar}>
              Guardar
            </Button>
          )}
        </Space>
      }
    >
      {error && (
        <FctAviso tipo="error" titulo="No se ha podido completar la operación">
          {error}
        </FctAviso>
      )}
      {contenido}
    </GcPagina>
  );

  if (!ficha || !perfilFicha) {
    return pagina(!error && <Spin aria-label="Cargando el paso de cliente" />);
  }

  const codigoFicha = codigoCl(ficha.cl);
  const movilesActuales = valoresActuales(comparacion?.campos.find((f) => f.clave === 'movil')?.actual ?? null);
  const perfilCambia = comparacion !== null && !mismoPerfil(perfil, perfilActualDe(comparacion));

  // Una fila del perfil de compra: las etiquetas de la ficha, un paso y el selector de la columna de Gescom.
  const filaPerfil = (etiqueta: string, izquierda: string, hayIzquierda: boolean, coincide: boolean, pasar: () => void, selector: ReactNode) => (
    <tr key={etiqueta}>
      <th scope="row">{etiqueta}</th>
      <td>{izquierda}</td>
      <td>
        <Button size="small" aria-label={`Pasar ${etiqueta} de la ficha a Gescom`} onClick={pasar} disabled={!hayIzquierda || coincide}>
          →
        </Button>
      </td>
      <td>{selector}</td>
      <td>
        <Marca izquierda={hayIzquierda} coincide={coincide} />
      </td>
    </tr>
  );

  return pagina(
    <>
      {aviso && <FctAviso tipo="aviso" titulo={aviso} />}
      {cambiados && (
        <FctAviso tipo="aviso" titulo="Gescom ha cambiado este cliente después de cargarlo">
          No se ha guardado nada. Ha cambiado: {cambiados.length > 0 ? cambiados.join(', ') : 'otros datos'}. Esos campos muestran ya el valor
          nuevo de Gescom; el resto de lo que había editado se mantiene. Revise y guarde de nuevo.
        </FctAviso>
      )}

      {vinculado?.guardado && (
        <FctAviso tipo="exito" titulo={guardado ? 'Cliente guardado en Gescom' : 'Este caso ya tiene su cliente guardado en Gescom'}>
          {vinculado.codigo || `nº ${vinculado.idCliente}`} · {vinculado.nombreCompleto ?? SIN_DATO}.{' '}
          {guardado?.yaGuardado ? 'Ese mismo guardado ya estaba hecho: no se ha vuelto a escribir nada. ' : ''}
          Puede corregirlo y guardarlo de nuevo.
        </FctAviso>
      )}

      <table className="ferias-cliente" aria-label="Ficha y cliente de Gescom">
        <thead>
          <tr className="ferias-cliente__cabecera">
            <th scope="col" className="ferias-cliente__campo">
              Campo
            </th>
            <th scope="col">Ficha reunión</th>
            <th scope="col">
              <Button
                className="ferias-cliente__pasar-todos"
                aria-label="Pasar todos los distintos de la ficha a Gescom"
                title="Pasar todos los distintos de la ficha a Gescom"
                onClick={pasarTodos}
              >
                →
              </Button>
            </th>
            <th scope="col">{comparacion ? 'Gescom' : 'Gescom (cliente nuevo)'}</th>
            <th scope="col">
              <span className="ferias-oculto">Coincidencia</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">Código CL</th>
            <td className="fct-mono">{codigoFicha ?? SIN_DATO}</td>
            <td />
            <td>
              <Space>
                <span className="fct-mono">{comparacion ? comparacion.codigo || 'Sin código' : 'Nuevo: el código se asigna al guardar'}</span>
                <Button
                  size="small"
                  aria-label="Buscar el cliente en Gescom"
                  title="Buscar el cliente en Gescom"
                  onClick={() => setBuscador({ abierto: true, iniciales: null })}
                >
                  <FctIcono nombre="buscar" />
                </Button>
                {comparacion && (
                  <Button size="small" aria-label="Quitar el cliente y crear uno nuevo" title="Quitar el cliente y crear uno nuevo" onClick={() => nuevo(ficha)}>
                    <FctIcono nombre="cerrar" />
                  </Button>
                )}
              </Space>
            </td>
            <td>
              {comparacion && (
                <Marca izquierda={codigoFicha !== null} coincide={codigoFicha !== null && mismoValor('cl', codigoFicha, comparacion.codigo ?? '')} />
              )}
            </td>
          </tr>

          {FILAS.map(({ clave, etiqueta, mono }) => {
            const izquierda = texto(ficha[clave]);
            const coincide = mismoValor(clave, izquierda, derecha[clave] ?? '');
            return (
              <tr key={clave}>
                <th scope="row">{etiqueta}</th>
                <td className={mono ? 'fct-mono' : undefined}>{izquierda || SIN_DATO}</td>
                <td>
                  <Button
                    size="small"
                    aria-label={`Pasar ${etiqueta} de la ficha a Gescom`}
                    onClick={() => poner(clave, izquierda)}
                    disabled={izquierda === '' || coincide}
                  >
                    →
                  </Button>
                </td>
                <td>
                  <Input
                    aria-label={`${etiqueta} en Gescom`}
                    className={mono ? 'fct-mono' : undefined}
                    value={derecha[clave] ?? ''}
                    onChange={(e) => poner(clave, e.target.value)}
                  />
                  {clave === 'movil' && comparacion && movilesActuales.length > 0 && (
                    <div className="ferias-cliente__nota">
                      Uno distinto se sustituirá por el móvil actual, {movilesActuales[0]}.
                      {movilesActuales.length > 1 && ` Los otros móviles en Gescom (${movilesActuales.slice(1).join(', ')}) no cambian.`}
                    </div>
                  )}
                  {clave === 'nacimiento' && comparacion && <div className="ferias-cliente__nota">Gescom guarda el año.</div>}
                </td>
                <td>
                  <Marca izquierda={izquierda !== ''} coincide={coincide} />
                </td>
              </tr>
            );
          })}

          {filaPerfil(
            'Plazo de compra',
            perfilFicha.plazo ? PLAZOS[perfilFicha.plazo] : SIN_DATO,
            perfilFicha.plazo !== null,
            perfilFicha.plazo === perfil.plazo,
            () => setPerfil({ ...perfil, plazo: perfilFicha.plazo }),
            <Select
              aria-label="Plazo de compra en Gescom"
              allowClear
              placeholder="Sin plazo marcado"
              style={{ minWidth: 200 }}
              value={perfil.plazo ?? undefined}
              options={opciones(PLAZOS)}
              onChange={(plazo?: number) => setPerfil({ ...perfil, plazo: plazo ?? null })}
            />,
          )}
          {filaPerfil(
            'Tramos de facturación',
            etiquetas(TRAMOS, perfilFicha.tramos),
            perfilFicha.tramos.length > 0,
            mismoPerfil({ plazo: null, tramos: perfilFicha.tramos, provincias: [] }, { plazo: null, tramos: perfil.tramos, provincias: [] }),
            () => setPerfil({ ...perfil, tramos: perfilFicha.tramos }),
            <Select
              aria-label="Tramos de facturación en Gescom"
              mode="multiple"
              placeholder="Ningún tramo"
              style={{ width: '100%' }}
              value={perfil.tramos}
              options={opciones(TRAMOS)}
              onChange={(tramos: number[]) => setPerfil({ ...perfil, tramos })}
            />,
          )}
          {filaPerfil(
            'Provincias',
            etiquetas(PROVINCIAS, perfilFicha.provincias),
            perfilFicha.provincias.length > 0,
            mismoPerfil({ plazo: null, tramos: [], provincias: perfilFicha.provincias }, { plazo: null, tramos: [], provincias: perfil.provincias }),
            () => setPerfil({ ...perfil, provincias: perfilFicha.provincias }),
            <Select
              aria-label="Provincias en Gescom"
              mode="multiple"
              placeholder="Ninguna provincia"
              style={{ width: '100%' }}
              value={perfil.provincias}
              options={opciones(PROVINCIAS)}
              optionFilterProp="label"
              onChange={(provincias: number[]) => setPerfil({ ...perfil, provincias })}
            />,
          )}

          {FILAS_INFORMATIVAS.map(({ clave, etiqueta, mono }) => (
            <tr key={clave} className="ferias-cliente__informativa">
              <th scope="row">{etiqueta}</th>
              <td className={mono ? 'fct-mono' : undefined}>{texto(ficha[clave]) || SIN_DATO}</td>
              <td />
              <td>Solo informativo: no se guarda en Gescom</td>
              <td />
            </tr>
          ))}
        </tbody>
      </table>

      {perfilFicha.sinEquivalente.length > 0 && (
        <FctAviso tipo="aviso" titulo="Provincias de la ficha sin equivalente">
          Gescom no reconoce: {perfilFicha.sinEquivalente.join(', ')}.
        </FctAviso>
      )}
      {comparacion && (
        <p>
          {perfilCambia ? 'El perfil de compra de Gescom se sustituirá por el de la columna de Gescom.' : 'El perfil de compra de Gescom no cambia.'}{' '}
          Un campo vacío no borra nada en Gescom.
        </p>
      )}
      {perfilCambia && comparacion?.perfil.dadoDeBaja && (
        <FctAviso tipo="aviso" titulo="Este inversor está dado de baja">
          Guardar el perfil de compra lo vuelve a dar de alta en Gescom.
        </FctAviso>
      )}

      <BuscadorCliente
        idCaso={idCaso}
        abierto={buscador.abierto}
        criterios={criterios}
        iniciales={buscador.iniciales}
        onElegir={(id) => void cargarCliente(id, ficha)}
        onCerrar={() => setBuscador({ abierto: false, iniciales: null })}
      />
      <ConfirmarGuardado accion={accion} guardando={guardando} error={errorGuardado} onConfirmar={() => void confirmar()} onCancelar={() => setAccion(null)} />
    </>,
  );
};

export default ClienteCasoPagina;
